import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { HttpService } from 'src/app/services/http.service';
import { LanguageService } from 'src/app/services/language.service';
import { SettingsService } from 'src/app/services/settings.service';
import { UtilsService } from 'src/app/services/utils.service';
import { ListFields } from '../../listfields';
import { Configuration, ConfigurationParsed } from '../configuration';
import { forkJoin } from 'rxjs';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MultipleSearchPropertyPipe } from 'src/app/pipes/multiple-search-property.pipe';

/** List view of the bike configurations */

@Component({
  selector: 'app-configuration-list',
  templateUrl: './configuration-list.component.html',
  styleUrls: ['./configuration-list.component.scss'],
  standalone: false
})
export class ConfigurationListComponent implements OnInit {

  /** Define field list for sorting and grouping */
  public configurationAllFields: ListFields[] = [
    { Id: 0, FieldName: "Id", Uppercase: false, DisplayName: this.languageService.languageJSON.ConfigurationList_ConfigId, SearchBy: true, GroupBy: true },
    { Id: 1, FieldName: "ConfigName", Uppercase: false, DisplayName: this.languageService.languageJSON.ConfigurationList_ConfigName, SearchBy: true, GroupBy: true },
    { Id: 2, FieldName: "OEMName", Uppercase: false, DisplayName: this.languageService.languageJSON.ComponentDetails_OEMName, SearchBy: true, GroupBy: true },
    { Id: 3, FieldName: "BikeBrand", Uppercase: false, DisplayName: this.languageService.languageJSON.Global_BikeBrand, SearchBy: true, GroupBy: true },
    { Id: 4, FieldName: "BikeType", Uppercase: false, DisplayName: this.languageService.languageJSON.ComponentDetails_BikeType, SearchBy: true, GroupBy: true },
    { Id: 5, FieldName: "BikeModelName", Uppercase: false, DisplayName: this.languageService.languageJSON.ConfigurationDetails_BikeModel, SearchBy: true, GroupBy: true }
  ];

  public configurationList: Configuration[] = [];
  public brandOptionsList: { id: number, name: string }[] = [];
  public oeOptionsList: { id: number, name: string }[] = [];
  public bikeTypeOptionsList: { id: number, name: string }[] = [];
  public componentClassOptionsList: { id: number, name: string }[] = [];

  public loading: boolean = true;
  public order: string = this.configurationAllFields[0].FieldName;
  public reverse: boolean = false;
  public caseInsensitive: boolean = true;
  public searchByList: any = {};
  public customizeDropdownVisible: boolean = false;

  public page: number = 1;
  public totalItems: number = 0;
  public itemsPerPage: number = this.settingsService.getItemsPerPage();

  public selectedColumnsGroup: FormGroup = this.fb.group({
    selectedColumns: new FormControl([]),
  });

  get configurationFields(): ListFields[] {
    let manageBikesFieldsArray: ListFields[] = [];
    this.selectedColumns?.value.forEach((el: number) => {
      manageBikesFieldsArray.push(this.configurationAllFields[el]);
    });
    return manageBikesFieldsArray;
  }

  get selectedColumns() {
    return this.selectedColumnsGroup.get("selectedColumns");
  }

  constructor(public languageService: LanguageService, private httpService: HttpService,
    private router: Router, private settingsService: SettingsService, private fb: FormBuilder,
    private utilsService: UtilsService, private multipleSearchPropertyPipe: MultipleSearchPropertyPipe) { }

  /** Initializing configurations, initializing options */
  ngOnInit(): void {
    this.loading = true;
    this.selectedColumns?.setValue([0, 1, 2]);

    if (localStorage.getItem("configurationsSelectedColumns")) {
      this.selectedColumns?.setValue(JSON.parse(localStorage.getItem("configurationsSelectedColumns")!));
    }

    forkJoin([
      this.httpService.getBikeTypeOptionsList(),
      this.httpService.getOEOptionsList(1, 0),
      this.httpService.getBrandOptionsList(),
      this.httpService.getComponentClassOptionsList()
    ]).subscribe((resp) => {
      this.bikeTypeOptionsList = resp[0];
      this.oeOptionsList = resp[1];
      this.brandOptionsList = resp[2];
      this.componentClassOptionsList = resp[3];

      this.initConfigurations();
    });
  }

  /** Changing table ordering by selected field */
  setOrder(value: string, groupBy: boolean) {
    if (groupBy) {
      if (this.order === value) {
        this.reverse = !this.reverse;
      }

      this.order = value;
    }
  }

  /** Getting configuration list from Api endpoint */
  private initConfigurations(): void {
    this.httpService.getConfigurationList().subscribe((resp: any) => {
      this.loading = false;
      if (resp?.success) {
        this.configurationList = resp.data.map((el: any) => {
          const configurationEL: ConfigurationParsed = {
            Id: el.id,
            BikeCount: el.bikes_count,
            ConfigName: el.config_name,
            ConfigId: el.config_id,
            OE: el.oe,
            OEMName: this.utilsService.getOEMName(el.oe, this.oeOptionsList),
            Brand: el.brand,
            BikeBrand: this.brandOptionsList.find((brandEl) => { return brandEl.id == el.brand_id })?.name,
            BikeType: this.bikeTypeOptionsList.find((bikeTypeEl) => { return bikeTypeEl.id == el.bike_type })?.name,
            Comment: el.comment,
            BikeModelName: el.bike_model_name
          }

          return configurationEL;
        });

        this.totalItems = this.configurationList.length;
      }
    }, (error) => {
      this.loading = false;
    });
  }

  public columnsSelectionChanged() {
    for (let i = 0; i < this.configurationAllFields.length; i++) {
      if (!this.selectedColumns?.value.includes(i) && this.searchByList[this.configurationAllFields[i].FieldName!]) {
        delete this.searchByList[this.configurationAllFields[i].FieldName!];
        this.searchByList = { ... this.searchByList };
      }
    }
    localStorage.setItem("configurationsSelectedColumns", JSON.stringify(this.selectedColumns?.value));
  }

  /** Opens view configuration page */
  public viewConfiguration(configuration: Configuration) {
    this.router.navigate(['/view-configuration'], { queryParams: { id: configuration.Id } });
  }

  /** Opens edit bikes linked with this configuration page */
  public editBikes(configuration: Configuration) {
    this.router.navigate(['/assign-bikes'], { queryParams: { configurationId: configuration.Id } });
  }

  /** Opens manage bikes with filtering the selected configuration */
  public viewBikes(configuration: Configuration) {
    this.router.navigate(['/bike-overview'], { queryParams: { configurationId: configuration.Id } });
  }

  public hasAnySearchElement(): boolean {
    return Object.keys(this.searchByList).length != 0;
  }

  public onSearchChanged(event: any) {
    this.searchByList = event;
    this.totalItems = this.multipleSearchPropertyPipe.transform(this.configurationList, this.searchByList).length;
  }

  public getPaginationText(): string {
    return this.utilsService.getPaginationText(this.itemsPerPage, this.page, this.totalItems);
  }

  public onPageChangeEvent(event: number) {
    this.page = event;
  }
}
